import {
  Alert,
  AlertDescription,
  AlertTitle,
  ApiErrorState,
  Badge,
  Button,
  FieldArray,
  FieldArrayItem,
  FieldHint,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormLayout,
  FormMessage,
  FormSection,
  Input,
  MarkdownInput,
  MobileNavDrillProvider,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  SwitchCard,
  toast,
  useMobileNavDrillTop,
  VersionBumpSelector,
} from '@avila-roffe/components-web';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  BookOpen,
  MessageSquare,
  RotateCcw,
  SlidersHorizontal,
  Wrench,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type Control, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { usePageHeader } from '../../page-header/PageHeaderContext';
import { useAllSkillReleasesFlat } from '../skills/hooks/use-skills';
import { useTools } from '../tools/hooks/use-tools';
import type { CatalogItem } from './AgentRefsBindingPanel';
import { createAgentRelease, updateAgentRelease } from './agentApi';
import {
  CAPABILITIES,
  CapabilityLegendPopover,
  CapabilityToggle,
  ModelSelectPopover,
  modelCapabilityKeys,
} from './components/model-picker';
import { SkillReleasePickerDialog } from './components/SkillReleasePickerDialog';
import { ToolSkillPickerDialog } from './components/ToolSkillPickerDialog';
import { agentDetailKeys, useAgentDetail, useAgentReleases } from './hooks/use-agent-detail';
import { useModelRegistry } from './hooks/use-agents';
import type { Agent } from './types';

// ─── Schema ───────────────────────────────────────────────────────────────────

const schema = z.object({
  bump: z.enum(['major', 'minor', 'patch']),
  beta: z.boolean(),
  notes: z.string(),
  modelAlias: z.string().min(1, 'Required'),
  requiredCapabilities: z.array(z.string()),
  systemPrompt: z.string(),
  toolRefs: z.array(z.string()),
  skillReleaseRefs: z.array(z.string()),
  initialSalutation: z.boolean(),
  salutationExamples: z.array(z.object({ value: z.string() })),
  approvalMode: z.enum(['NONE', 'REQUIRED', 'SUGGESTED']),
  maxToolCalls: z.coerce.number().int().min(1),
  maxSteps: z.coerce.number().int().min(1),
});

type FormValues = z.infer<typeof schema>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

type BumpType = 'major' | 'minor' | 'patch';

function parseVersion(name: string): [number, number, number] {
  const m = name.match(/v?(\d+)\.(\d+)\.(\d+)/);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : [1, 0, 0];
}

function parseBetaNumber(name: string): { base: [number, number, number]; n: number } | null {
  const m = name.match(/v?(\d+)\.(\d+)\.(\d+)-beta\.(\d+)/);
  return m ? { base: [Number(m[1]), Number(m[2]), Number(m[3])], n: Number(m[4]) } : null;
}

function bumpVersion(base: [number, number, number], bump: BumpType): [number, number, number] {
  const [maj, min, pat] = base;
  if (bump === 'major') return [maj + 1, 0, 0];
  if (bump === 'minor') return [maj, min + 1, 0];
  return [maj, min, pat + 1];
}

function isBetaContinuation(base: [number, number, number], bump: BumpType): boolean {
  const [, min, pat] = base;
  if (bump === 'major') return min === 0 && pat === 0;
  if (bump === 'minor') return min > 0 && pat === 0;
  return pat > 0;
}

function buildReleaseName(
  base: [number, number, number],
  bump: BumpType,
  beta: boolean,
  latestName: string,
): string {
  const latest = parseBetaNumber(latestName);
  if (beta && latest && isBetaContinuation(latest.base, bump)) {
    const [maj, min, pat] = latest.base;
    return `v${maj}.${min}.${pat}-beta.${latest.n + 1}`;
  }
  const [maj, min, pat] = bumpVersion(base, bump);
  return beta ? `v${maj}.${min}.${pat}-beta.1` : `v${maj}.${min}.${pat}`;
}

const BUMP_OPTIONS = [
  { value: 'patch', label: 'Patch', description: 'Bug fixes and minor corrections' },
  { value: 'minor', label: 'Minor', description: 'New features, backwards-compatible' },
  { value: 'major', label: 'Major', description: 'Breaking changes or major reworks' },
] as const;

function buildDefaults(agent: Agent): FormValues {
  return {
    bump: 'patch',
    beta: false,
    notes: '',
    modelAlias: agent.modelAlias,
    requiredCapabilities: [...(agent.guardrails.requiredCapabilities ?? [])],
    systemPrompt: agent.systemPrompt ?? '',
    toolRefs: [...agent.toolRefs],
    skillReleaseRefs: [],
    initialSalutation: agent.interface.initialSalutation ?? false,
    salutationExamples: (agent.interface.salutationExamples ?? []).map((v) => ({ value: v })),
    approvalMode: agent.guardrails.approvalMode ?? 'SUGGESTED',
    maxToolCalls: agent.guardrails.maxToolCalls ?? 20,
    maxSteps: agent.guardrails.maxSteps ?? 50,
  };
}

// ─── Salutation examples array ────────────────────────────────────────────────

function SalutationExamplesField({ control }: { control: Control<FormValues> }) {
  const { fields, append, remove } = useFieldArray({ control, name: 'salutationExamples' });
  return (
    <FieldArray
      label="Salutation examples"
      description="The agent picks one of these at random to open the conversation."
      addLabel="Add example"
      onAdd={() => append({ value: '' })}
      itemCount={fields.length}
    >
      {fields.map((field, index) => (
        <FieldArrayItem key={field.id} onRemove={() => remove(index)}>
          <FormField
            control={control}
            name={`salutationExamples.${index}.value`}
            render={({ field: f }) => (
              <FormItem>
                <FormControl>
                  <Input {...f} placeholder={`Example ${index + 1}`} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldArrayItem>
      ))}
    </FieldArray>
  );
}

// ─── Inner page ───────────────────────────────────────────────────────────────

function NewReleasePageInner({ tenantSlug = '' }: { tenantSlug?: string }) {
  const { id: agentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const drillTop = useMobileNavDrillTop();

  const { data: agent, isLoading, error } = useAgentDetail(agentId);
  const { data: releases = [] } = useAgentReleases(agentId);
  const { data: registry = [] } = useModelRegistry();
  const { data: allTools = [], isLoading: toolsLoading } = useTools();

  const [toolPickerOpen, setToolPickerOpen] = useState(false);
  const [skillReleasePickerOpen, setSkillReleasePickerOpen] = useState(false);
  const [skillsWithNoRelease, setSkillsWithNoRelease] = useState<string[]>([]);
  const autoFillDone = useRef(false);

  const { data: allSkillReleases = [], isLoading: releasesLoading } = useAllSkillReleasesFlat();

  const latestName = releases[0]?.name ?? 'v1.0.0';
  const base = useMemo(() => parseVersion(latestName), [latestName]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      bump: 'patch',
      beta: false,
      notes: '',
      modelAlias: '',
      requiredCapabilities: [],
      systemPrompt: '',
      toolRefs: [],
      skillReleaseRefs: [],
      initialSalutation: false,
      salutationExamples: [],
      approvalMode: 'SUGGESTED',
      maxToolCalls: 20,
      maxSteps: 50,
    },
    values: agent ? buildDefaults(agent) : undefined,
  });

  // Pre-fill skill release refs: for each agent skill, pick the latest PUBLISHED release.
  useEffect(() => {
    if (autoFillDone.current) return;
    if (!agent || releasesLoading) return;
    autoFillDone.current = true;

    if (agent.skillRefs.length === 0) return;

    const skillDisplayNames = new Map<string, string>();
    const publishedBySkillName = new Map<string, { id: string; createdAt: string }[]>();

    for (const f of allSkillReleases) {
      skillDisplayNames.set(f.skillName, f.skillDisplayName || f.skillName);
      if (f.releaseStatus === 'PUBLISHED') {
        const arr = publishedBySkillName.get(f.skillName) ?? [];
        arr.push({ id: f.releaseId, createdAt: f.createdAt });
        publishedBySkillName.set(f.skillName, arr);
      }
    }

    const autoFilled: string[] = [];
    const missing: string[] = [];

    for (const skillRef of agent.skillRefs) {
      const published = publishedBySkillName.get(skillRef);
      if (!published?.length) {
        missing.push(skillDisplayNames.get(skillRef) ?? skillRef);
      } else {
        const latest = [...published].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
        autoFilled.push(latest.id);
      }
    }

    if (autoFilled.length > 0) {
      form.setValue('skillReleaseRefs', autoFilled, { shouldDirty: false });
    }
    if (missing.length > 0) {
      setSkillsWithNoRelease(missing);
    }
  }, [agent, releasesLoading, allSkillReleases, form]);

  const watchBump = form.watch('bump') as BumpType;
  const watchBeta = form.watch('beta');
  const watchModelAlias = form.watch('modelAlias');
  const watchToolRefs = form.watch('toolRefs');
  const watchSkillReleaseRefs = form.watch('skillReleaseRefs');
  const watchRequiredCaps = form.watch('requiredCapabilities');

  const releaseName = buildReleaseName(base, watchBump, watchBeta, latestName);

  const bumpOptions = BUMP_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
    description: opt.description,
    preview: buildReleaseName(base, opt.value, watchBeta, latestName),
  }));

  const selectedModel = registry.find((m) => m.modelName === watchModelAlias) ?? null;
  const modelSupportsTools = selectedModel?.supportsTools === true;

  const { filteredModels, filteredProviders } = useMemo(() => {
    const requiredModelCaps = modelCapabilityKeys(watchRequiredCaps);
    const filtered = registry.filter((m) => {
      for (const cap of requiredModelCaps) {
        if (m[cap] !== true) return false;
      }
      return true;
    });
    return {
      filteredModels: filtered,
      filteredProviders: [...new Set(filtered.map((m) => m.provider ?? 'Unknown'))],
    };
  }, [registry, watchRequiredCaps]);

  // Build tool catalog and group selected tools by prefix (e.g. "ado" from "ado.get_workitem")
  const toolCatalog = useMemo<CatalogItem[]>(
    () =>
      allTools.map((t) => ({
        value: t.name,
        label: (t.interface as { displayName?: string }).displayName || t.name,
        meta: t.appSlug,
        href: `/tools/${t.id}`,
        scope: t.scope,
        description: t.description,
        status: t.status,
      })),
    [allTools],
  );

  const groupedTools = useMemo(() => {
    const groups = new Map<string, CatalogItem[]>();
    for (const ref of watchToolRefs ?? []) {
      const item = toolCatalog.find((t) => t.value === ref) ?? { value: ref, label: ref };
      const prefix = ref.includes('.') ? ref.split('.')[0] : 'other';
      if (!groups.has(prefix)) groups.set(prefix, []);
      groups.get(prefix)?.push(item);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [watchToolRefs, toolCatalog]);

  // Build an index of skill release ID → { skillDisplayName, releaseName }
  const skillReleaseIndex = useMemo(() => {
    const index = new Map<
      string,
      { skillDisplayName: string; releaseName: string; skillId: string }
    >();
    for (const f of allSkillReleases) {
      index.set(f.releaseId, {
        skillDisplayName: f.skillDisplayName || f.skillName,
        releaseName: f.releaseName,
        skillId: f.skillId,
      });
    }
    return index;
  }, [allSkillReleases]);

  // Group selected skill releases by skill for display
  const groupedSkillReleases = useMemo(() => {
    const groups = new Map<
      string,
      { skillDisplayName: string; releaseId: string; releaseName: string }[]
    >();
    for (const id of watchSkillReleaseRefs ?? []) {
      const entry = skillReleaseIndex.get(id);
      if (!entry) continue;
      if (!groups.has(entry.skillId)) groups.set(entry.skillId, []);
      groups.get(entry.skillId)?.push({
        skillDisplayName: entry.skillDisplayName,
        releaseId: id,
        releaseName: entry.releaseName,
      });
    }
    return [...groups.values()];
  }, [watchSkillReleaseRefs, skillReleaseIndex]);

  const createAndConfigure = useMutation({
    mutationFn: async (data: FormValues) => {
      const name = buildReleaseName(base, data.bump, data.beta, latestName);

      const release = await createAgentRelease(agentId ?? '', {
        name,
        notes: data.notes.trim() || undefined,
      });
      if (!release) throw new Error('Failed to create release');

      await updateAgentRelease(agentId ?? '', release.id, {
        systemPrompt: data.systemPrompt,
        modelAlias: data.modelAlias,
        toolRefs: data.toolRefs,
        skillRefs: data.skillReleaseRefs,
        initialSalutation: data.initialSalutation,
        salutationExamples: data.salutationExamples.map((e) => e.value).filter(Boolean),
        guardrails: {
          approvalMode: data.approvalMode,
          maxToolCalls: data.maxToolCalls,
          maxSteps: data.maxSteps,
          requiredCapabilities: data.requiredCapabilities,
        },
      });

      return release;
    },
    onSuccess: (release) => {
      void qc.invalidateQueries({ queryKey: agentDetailKeys.releases(agentId ?? '') });
      toast.success(`Release ${release.name} created`);
      navigate(`/agents/${agentId}/releases/${release.id}`);
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : 'Failed to create release');
    },
  });

  usePageHeader({
    title: 'New Release',
    subtitle: agent ? `${agent.interface.displayName || agent.name} · ${releaseName}` : undefined,
    backPath: `/agents/${agentId}?tab=releases`,
    mobileOnBack: drillTop?.onBack,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholder
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return <ApiErrorState message={error?.message ?? 'Agent not found.'} className="my-8" />;
  }

  async function onSubmit(data: FormValues) {
    await createAndConfigure.mutateAsync(data);
  }

  return (
    <Form {...form}>
      <FormLayout
        actionsPosition="sticky"
        isDirty
        className="px-4"
        cancelAction={{
          label: 'Cancel',
          type: 'button',
          onClick: () => navigate(`/agents/${agentId}?tab=releases`),
        }}
        submitAction={{
          label: createAndConfigure.isPending ? 'Creating…' : `Create ${releaseName}`,
          type: 'button',
          disabled: createAndConfigure.isPending,
          onClick: () => void form.handleSubmit(onSubmit)(),
        }}
      >
        {/* ── Reset banner ─────────────────────────────────────────────── */}
        {form.formState.isDirty && (
          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            <span>Form differs from agent&apos;s saved values.</span>
            <Button
              type="button"
              size="sm"
              appearance="ghost"
              className="h-7 gap-1.5 text-xs"
              onClick={() => form.reset(buildDefaults(agent))}
            >
              <RotateCcw className="size-3" />
              Reset all to agent
            </Button>
          </div>
        )}

        {/* ── Version ─────────────────────────────────────────────────────── */}
        <FormSection title="Version" density="compact" columns={1} asCard>
          <FormField
            control={form.control}
            name="bump"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <VersionBumpSelector
                    options={bumpOptions}
                    value={field.value}
                    onValueChange={(v) => field.onChange(v as BumpType)}
                    beta={watchBeta}
                    onBetaChange={(v) => form.setValue('beta', v)}
                    betaLabel="Beta release"
                    betaDescription={
                      <>
                        Appends <code className="font-mono text-xs">-beta.1</code> to the version
                      </>
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {/* ── Notes ───────────────────────────────────────────────────────── */}
        <FormSection title="Notes" density="compact" columns={1} asCard>
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Release notes</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Optional release notes" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {/* ── Model ───────────────────────────────────────────────────────── */}
        <FormSection title="Model" asCard>
          <FormField
            control={form.control}
            name="requiredCapabilities"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-1 mb-0.5">
                      <p className="text-sm font-medium">Required capabilities</p>
                      <CapabilityLegendPopover />
                    </div>
                    <p className="mb-3 text-xs text-muted-foreground">
                      Toggle the capabilities this release requires. Only compatible models are
                      shown below.
                    </p>
                  </div>
                  {(agent.guardrails.requiredCapabilities ?? []).length > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      appearance="ghost"
                      className="shrink-0 text-xs text-muted-foreground"
                      onClick={() =>
                        field.onChange([...(agent.guardrails.requiredCapabilities ?? [])])
                      }
                    >
                      Reset
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {CAPABILITIES.map((cap) => (
                    <CapabilityToggle
                      key={cap.key}
                      cap={cap}
                      selected={field.value.includes(cap.key)}
                      onToggle={() => {
                        const next = field.value.includes(cap.key)
                          ? field.value.filter((k) => k !== cap.key)
                          : [...field.value, cap.key];
                        field.onChange(next);
                      }}
                    />
                  ))}
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="modelAlias"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Model</FormLabel>
                {registry.length > 0 ? (
                  <FormControl>
                    <ModelSelectPopover
                      value={field.value || undefined}
                      onChange={(v) => field.onChange(v ?? '')}
                      models={filteredModels}
                      providers={filteredProviders}
                    />
                  </FormControl>
                ) : (
                  <FormControl>
                    <Input {...field} placeholder="e.g. claude-sonnet-4-6" />
                  </FormControl>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {/* ── Tools ───────────────────────────────────────────────────────── */}
        {modelSupportsTools && (
          <FormSection title="Tools" asCard columns={1}>
            <FormField
              control={form.control}
              name="toolRefs"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      {field.value.length} tool{field.value.length !== 1 ? 's' : ''} enabled
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      appearance="outline"
                      onClick={() => setToolPickerOpen(true)}
                    >
                      <SlidersHorizontal className="size-3.5 mr-1.5" />
                      Manage Tools
                    </Button>
                  </div>

                  {groupedTools.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed py-8 text-center">
                      <div className="flex size-9 items-center justify-center rounded-full bg-muted">
                        <Wrench className="size-4 text-muted-foreground" />
                      </div>
                      <p className="text-sm text-muted-foreground">No tools enabled</p>
                      <Button
                        type="button"
                        size="sm"
                        appearance="ghost"
                        onClick={() => setToolPickerOpen(true)}
                      >
                        Add tools
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {groupedTools.map(([prefix, items]) => (
                        <div key={prefix}>
                          <p className="text-[11px] font-mono font-medium text-muted-foreground mb-1.5">
                            {prefix}.*
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {items.map((item) => (
                              <Badge
                                key={item.value}
                                variant="secondary"
                                appearance="soft"
                                className="font-mono text-xs gap-1.5 pr-1"
                              >
                                <Wrench className="size-3" />
                                {item.label !== item.value ? item.label : item.value}
                                <button
                                  type="button"
                                  aria-label={`Remove ${item.value}`}
                                  onClick={() =>
                                    field.onChange(field.value.filter((r) => r !== item.value))
                                  }
                                  className="ml-0.5 rounded-full opacity-60 hover:opacity-100"
                                >
                                  <X className="size-2.5" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <ToolSkillPickerDialog
                    open={toolPickerOpen}
                    onOpenChange={setToolPickerOpen}
                    title="Tools"
                    catalog={toolCatalog}
                    catalogLoading={toolsLoading}
                    selectedRefs={field.value}
                    isSaving={false}
                    onConfirm={async (newRefs) => {
                      field.onChange(newRefs);
                    }}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
        )}

        {/* ── Bound Skills ─────────────────────────────────────────────────── */}
        <FormSection title="Bound Skills" asCard columns={1}>
          {skillsWithNoRelease.length > 0 && (
            <Alert variant="warning" appearance="soft" icon={<AlertTriangle className="size-4" />}>
              <AlertTitle>No published release for some skills</AlertTitle>
              <AlertDescription>
                {skillsWithNoRelease.join(', ')} — publish a skill release first, then bind it
                manually here.
              </AlertDescription>
            </Alert>
          )}
          <FormField
            control={form.control}
            name="skillReleaseRefs"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    {field.value.length} skill release{field.value.length !== 1 ? 's' : ''} bound
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    appearance="outline"
                    onClick={() => setSkillReleasePickerOpen(true)}
                  >
                    <SlidersHorizontal className="size-3.5 mr-1.5" />
                    Manage
                  </Button>
                </div>

                {groupedSkillReleases.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed py-8 text-center">
                    <div className="flex size-9 items-center justify-center rounded-full bg-muted">
                      <BookOpen className="size-4 text-muted-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground">No skill releases bound</p>
                    <Button
                      type="button"
                      size="sm"
                      appearance="ghost"
                      onClick={() => setSkillReleasePickerOpen(true)}
                    >
                      Bind releases
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {groupedSkillReleases.map((items) =>
                      items.map((item) => (
                        <Badge
                          key={item.releaseId}
                          variant="secondary"
                          appearance="soft"
                          className="font-mono text-xs gap-1.5 pr-1"
                        >
                          <BookOpen className="size-3" />
                          {item.skillDisplayName}
                          <span className="text-muted-foreground">@{item.releaseName}</span>
                          <button
                            type="button"
                            aria-label={`Remove ${item.releaseName}`}
                            onClick={() =>
                              field.onChange(field.value.filter((id) => id !== item.releaseId))
                            }
                            className="ml-0.5 rounded-full opacity-60 hover:opacity-100"
                          >
                            <X className="size-2.5" />
                          </button>
                        </Badge>
                      )),
                    )}
                  </div>
                )}

                <SkillReleasePickerDialog
                  open={skillReleasePickerOpen}
                  onOpenChange={setSkillReleasePickerOpen}
                  selectedReleaseIds={field.value}
                  isSaving={false}
                  onConfirm={async (ids) => {
                    field.onChange(ids);
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {/* ── System Prompt ────────────────────────────────────────────────── */}
        <FormSection title="System Prompt" asCard>
          <FormField
            control={form.control}
            name="systemPrompt"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <MarkdownInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Write the system prompt…"
                    minHeight="360px"
                    expandLabel="System Prompt"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {/* ── Salutation ──────────────────────────────────────────────────── */}
        <FormSection title="Salutation" asCard>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="initialSalutation"
              render={({ field }) => (
                <SwitchCard
                  id="initialSalutation"
                  label="Agent greets first"
                  description="The agent initiates the conversation with a salutation instead of waiting for the user's first message."
                  icon={<MessageSquare className="size-5 text-muted-foreground" />}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <SalutationExamplesField control={form.control} />
          </div>
        </FormSection>

        {/* ── Guardrails ──────────────────────────────────────────────────── */}
        <FormSection title="Guardrails" asCard columns={3}>
          <FormField
            control={form.control}
            name="approvalMode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Approval Mode</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="NONE">None</SelectItem>
                    <SelectItem value="REQUIRED">Required</SelectItem>
                    <SelectItem value="SUGGESTED">Suggested</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="maxToolCalls"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Max Tool Calls
                  <FieldHint>Maximum tool invocations per run.</FieldHint>
                </FormLabel>
                <FormControl>
                  <Input type="number" min={1} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="maxSteps"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Max Steps
                  <FieldHint>Maximum reasoning cycles before forced stop.</FieldHint>
                </FormLabel>
                <FormControl>
                  <Input type="number" min={1} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>
      </FormLayout>
    </Form>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────

export function NewReleasePage({ tenantSlug = '' }: { tenantSlug?: string } = {}) {
  return (
    <MobileNavDrillProvider>
      <NewReleasePageInner tenantSlug={tenantSlug} />
    </MobileNavDrillProvider>
  );
}
